import React, { useEffect, useState } from 'react'
import { withTranslation as translate } from 'react-i18next'
import withConfig from '../../../services/withConfig'
import { DocusealForm } from '@docuseal/react'
import API from '../../../services/api'

// The member's own submission when memberships created one (so DocuSeal can
// remind them), else the template form, which creates a submission on opening
const signingForm = (signing, doc) => {
  if (signing.slugType === 'submitter') return { src: `https://docuseal.eu/s/${signing.slug}` }

  return {
    token: signing.token,
    src: `https://docuseal.eu/d/${doc.templateSlug}`,
    email: doc.email,
    name: `${doc.firstName} ${doc.lastName}`,
    externalId: doc.membershipId
  }
}

const DocumentModal = ({ doc, onClose, onError }) => {
  const [error, setError] = useState(null)
  // { token, slug, slugType }: slugType 'submitter' is the member's own DocuSeal
  // submission, created by memberships; otherwise the template form is signed
  // with the token
  const [signing, setSigning] = useState(null)

  const styles = getComputedStyle(window.document.documentElement)

  const theme = {
    primary: styles.getPropertyValue('--primary').trim()
  }

  useEffect(() => {
    const fetchToken = async () => {
      try {
        if (signing) return signing

        const params = {
          operator_id: doc.operatorId,
          membership_id: doc.membershipId,
          document_id: doc.documentId
        }

        const signingValue = await new Promise((resolve, reject) => {
          API.fetchSigningToken(params, (data) => {
            if (data.slug || data.token) resolve({ token: data.token, slug: data.slug, slugType: data.slug_type })
            else reject(new Error('Token not available', data))
          },
          reject
          )
        })
        setSigning(signingValue)
      } catch (err) {
        console.warn(err)
        setError('Failed getting document link')
      }
    }
    fetchToken()
  }, [doc, signing])

  const customCss = `
    .form-container.p-4 {
      padding: 8px;
    }
    label.field-name-label, .base-input {
      font-size: 20px !important;
      line-height: 28px !important;
    }
    label.field-name-label {
      margin-bottom: 12px;
    }
    .base-input {
      border-radius: 0.5em;
    }
    .submit-form-button {
      background-color: ${theme.primary};
      border-radius: 0.5em;
    }
  `

  const handleSigned = async (data) => {
    try {
      const params = {
        operator_id: doc.operatorId,
        membership_id: doc.membershipId,
        document_id: doc.documentId,
        id: doc.id,
        signed: true,
        data
      }

      await storeResponse(params)
      onClose(true)
    } catch (err) {
      console.warn(err)
      setError('Failed to store signature')
    }
  }

  const storeResponse = async (params) => {
    const resp = await new Promise((resolve, reject) => {
      API.signDocument(params, (data) => {
        resolve(data)
      },
      reject
      )
    })
    return resp
  }

  const handleLoad = () => {
    const formEl = document.querySelector('docuseal-form')

    if (!formEl) return

    const shadow = formEl.shadowRoot
    if (!shadow) return

    // add style to hide the form field container
    const style = document.createElement('style')
    style.id = 'scroll-gate'
    style.textContent = `
      .form-container { visibility: hidden !important; }
    `
    shadow.appendChild(style)

    // On a wide screen the docuseal-form element scrolls, on a phone DocuSeal
    // scrolls the document in its own #scrollbox inside the shadow root.
    // Scroll events don't bubble, so listen on the element and, in the capture
    // phase, on the shadow root, and check whichever element scrolled.
    const onScroll = (e) => {
      const el = e.target
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1000

      if (atBottom) {
        // remove style that is hiding the form field container
        const gate = shadow.getElementById('scroll-gate')
        if (gate) gate.remove()
        formEl.removeEventListener('scroll', onScroll)
        shadow.removeEventListener('scroll', onScroll, true)
      }
    }

    formEl.addEventListener('scroll', onScroll)
    shadow.addEventListener('scroll', onScroll, true)
  }

  if (error) {
    return (
      <div className='modal'>
        <div className='modal-content' onClick={(e) => e.stopPropagation()}>
          <p>{error}</p>
          <button className='off-button' onClick={() => onClose(false)}>Cancel</button>
        </div>
      </div>
    )
  }

  return (
    <div className='modal'>
      <div className='modal-content' onClick={(e) => e.stopPropagation()}>
        <h3>{doc.name}</h3>
        {/* Rendered once the token is in, so it loads the member's own submission */}
        {signing && (
          <DocusealForm
            {...signingForm(signing, doc)}
            autoScroll={false}
            withDecline={false}
            onComplete={handleSigned}
            customCss={customCss}
            orderAsOnPage={true} /* eslint-disable-line react/jsx-boolean-value */
            goToLast={false}
            values={{ FullName: `${doc.firstName} ${doc.lastName}` }}
            onLoad={handleLoad}
          />
        )}
        <div className='modal-center'>
          <button className='off-button' onClick={e => onClose(false)}>Cancel</button>
        </div>
      </div>
    </div>
  )
}

export default translate()(withConfig(DocumentModal))
